import React, { useState } from "react";
import clsx from "clsx";
import { FlaskConical, ArrowLeft } from "lucide-react";
import authService from "../services/authService";

const SignUpPage = ({ onSignUp, onBackToLogin }) => {
  const handleGoHome = () => {
    window.location.href = '/';
  };
  const [step, setStep] = useState("signup");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (!/[A-Za-z]/.test(formData.password)) {
      setError("Password must include at least one letter.");
      return;
    }

    if (!/[0-9]/.test(formData.password)) {
      setError("Password must include at least one number.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsLoading(true);

    try {
      const emailName = formData.email.split("@")[0];
      const result = await authService.signUp(
        formData.email,
        formData.password,
        formData.email,
        emailName
      );

      if (result.success) {
        if (result.needsConfirmation) {
          setStep("verify");
          setSuccessMessage("A verification code has been sent to your email.");
        } else {
          onSignUp();
        }
      } else {
        setError(result.error || "Sign up failed.");
      }
    } catch (err) {
      console.error("Sign up error:", err);
      setError("An error occurred during sign up.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    setError("");

    if (!verificationCode || verificationCode.length !== 6) {
      setError("Please enter the 6-digit verification code.");
      return;
    }

    setIsLoading(true);

    try {
      const result = await authService.confirmSignUp(formData.email, verificationCode);

      if (result.success) {
        setSuccessMessage("Email verified successfully!");

        const loginResult = await authService.signIn(formData.email, formData.password);

        if (loginResult.success) {
          const userInfo = { ...loginResult.user };

          localStorage.setItem("userInfo", JSON.stringify(userInfo));
          localStorage.setItem("authToken", loginResult.tokens.accessToken);
          localStorage.setItem("idToken", loginResult.tokens.idToken);
          localStorage.setItem("refreshToken", loginResult.tokens.refreshToken);
          localStorage.setItem("isLoggedIn", "true");
          localStorage.setItem("userRole", "user");

          window.dispatchEvent(new CustomEvent("userInfoUpdated"));
          onSignUp();
        } else {
          setTimeout(() => {
            onBackToLogin();
          }, 2000);
        }
      } else {
        setError(result.error || "Verification failed.");
      }
    } catch (err) {
      console.error("Verification error:", err);
      setError("An error occurred during verification.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError("");
    setSuccessMessage("");
    setIsLoading(true);

    try {
      const result = await authService.resendConfirmationCode(formData.email);

      if (result.success) {
        setSuccessMessage(result.message || "Verification code resent.");
      } else {
        setError(result.error || "Failed to resend verification code.");
      }
    } catch (err) {
      console.error("Resend code error:", err);
      setError("An error occurred while resending the code.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  // 회원가입 폼
  if (step === "signup") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-bg-100">
        {/* 홈 버튼 */}
        <button
          onClick={handleGoHome}
          className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2.5 rounded-xl text-text-300 hover:text-text-100 hover:bg-bg-200 transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium hidden sm:inline">Home</span>
        </button>

        {/* Sign up card */}
        <div className="max-w-md w-full">
          <div
            className="bg-bg-200/60 rounded-2xl overflow-hidden"
            style={{ border: "1px solid hsl(var(--border-300) / 0.12)" }}
          >
            <div className="p-8 sm:p-10">
              {/* Logo section */}
              <div className="flex flex-col items-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-sedaily-500 flex items-center justify-center mb-4">
                  <FlaskConical className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-text-100">Sign Up</h1>
                <p className="text-sm text-text-400 mt-1">Create a new account to get started</p>
              </div>

              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <InputField
                      id="email"
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      placeholder="Enter your email address"
                    />
                    {formData.email.length > 0 && (() => {
                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      const valid = emailRegex.test(formData.email);
                      return (
                        <p className={`mt-1.5 text-xs ${valid ? 'text-green-400' : 'text-red-400'}`}>
                          {valid ? '✓ Valid email address' : '* Please enter a valid email address (e.g. name@example.com)'}
                        </p>
                      );
                    })()}
                  </div>

                  <div>
                    <InputField
                      id="password"
                      label="Password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange("password", e.target.value)}
                      placeholder="Password (min. 8 characters)"
                    />
                    {(() => {
                      const p = formData.password;
                      const checks = [
                        { label: 'At least 8 characters', ok: p.length >= 8 },
                        { label: 'Letters (a-z)', ok: /[A-Za-z]/.test(p) },
                        { label: 'Number (0-9)', ok: /[0-9]/.test(p) },
                      ];
                      return (
                        <ul className="mt-2 space-y-1">
                          {checks.map(({ label, ok }) => (
                            <li key={label} className={`text-xs flex items-center gap-1.5 ${p.length === 0 ? 'text-text-500' : ok ? 'text-green-400' : 'text-red-400'}`}>
                              <span>{p.length === 0 ? '·' : ok ? '✓' : '✗'}</span>
                              {label}
                            </li>
                          ))}
                        </ul>
                      );
                    })()}
                  </div>

                  <div>
                    <InputField
                      id="confirmPassword"
                      label="Confirm Password"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                      placeholder="Re-enter your password"
                    />
                    {formData.confirmPassword.length > 0 && (
                      <p className={`mt-1.5 text-xs ${formData.confirmPassword === formData.password ? 'text-green-400' : 'text-red-400'}`}>
                        {formData.confirmPassword === formData.password ? '✓ Passwords match' : '✗ Passwords do not match'}
                      </p>
                    )}
                  </div>
                </div>

                {error && <ErrorMessage message={error} />}
                {successMessage && <SuccessMessage message={successMessage} />}

                <SubmitButton isLoading={isLoading} text={isLoading ? "Processing..." : "Sign Up"} />

                {/* Divider */}
                <div className="relative py-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full" style={{ borderTop: "1px solid hsl(var(--border-300) / 0.1)" }} />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="px-3 bg-bg-200 text-text-400">or</span>
                  </div>
                </div>

                {/* Sign in link */}
                <div className="text-center">
                  <span className="text-sm text-text-400">Already have an account? </span>
                  <button
                    type="button"
                    onClick={onBackToLogin}
                    className="text-sm font-semibold text-sedaily-400 hover:text-sedaily-300 transition-colors"
                  >
                    Sign in
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 이메일 인증 폼
  return (
    <div className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-bg-100">
      {/* 홈 버튼 */}
      <button
        onClick={handleGoHome}
        className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2.5 rounded-xl text-text-300 hover:text-text-100 hover:bg-bg-200 transition-all"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm font-medium hidden sm:inline">Home</span>
      </button>

      {/* 인증 카드 */}
      <div className="max-w-md w-full">
        <div
          className="bg-bg-200/60 rounded-2xl overflow-hidden"
          style={{ border: "1px solid hsl(var(--border-300) / 0.12)" }}
        >
          <div className="p-8 sm:p-10">
            {/* 로고 섹션 */}
            <div className="flex flex-col items-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-sedaily-500 flex items-center justify-center mb-4">
                <FlaskConical className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-text-100">Email Verification</h1>
              <p className="text-sm text-text-400 mt-1 text-center">
                Enter the 6-digit verification code<br />sent to {formData.email}
              </p>
            </div>

            <form className="space-y-5" onSubmit={handleVerification}>
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-text-200 mb-2">
                  Verification Code
                </label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  maxLength="6"
                  required
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ""))}
                  className="w-full px-4 py-3 bg-bg-300/50 text-text-100 text-center text-2xl tracking-widest rounded-xl outline-none focus:ring-1 focus:ring-sedaily-500/30 transition-all placeholder:text-text-400"
                  style={{ border: "1px solid hsl(var(--border-300) / 0.12)" }}
                  placeholder="000000"
                />
              </div>

              {error && <ErrorMessage message={error} />}
              {successMessage && <SuccessMessage message={successMessage} />}

              <div className="space-y-3">
                <SubmitButton isLoading={isLoading} text={isLoading ? "Verifying..." : "Verify"} />

                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isLoading}
                  className="w-full text-sm text-text-300 hover:text-text-100 transition-colors"
                >
                  Resend verification code
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

// 입력 필드 컴포넌트
const InputField = ({ id, label, type, value, onChange, placeholder }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-text-200 mb-2">
      {label}
    </label>
    <input
      id={id}
      name={id}
      type={type}
      required
      value={value}
      onChange={onChange}
      className="w-full px-4 py-3 bg-bg-300/50 text-text-100 rounded-xl outline-none focus:ring-1 focus:ring-sedaily-500/30 transition-all placeholder:text-text-400"
      style={{ border: "1px solid hsl(var(--border-300) / 0.12)" }}
      placeholder={placeholder}
    />
  </div>
);

// 에러 메시지 컴포넌트
const ErrorMessage = ({ message }) => (
  <div
    className="rounded-xl p-4 flex items-start gap-3 bg-red-500/10"
    style={{ border: "1px solid hsl(0 84.2% 60.2% / 0.2)" }}
  >
    <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
    <p className="text-sm flex-1 text-red-400">{message}</p>
  </div>
);

// 성공 메시지 컴포넌트
const SuccessMessage = ({ message }) => (
  <div
    className="rounded-xl p-4 flex items-start gap-3 bg-green-500/10"
    style={{ border: "1px solid hsl(142 76% 36% / 0.2)" }}
  >
    <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
    <p className="text-sm flex-1 text-green-400">{message}</p>
  </div>
);

// 제출 버튼 컴포넌트
const SubmitButton = ({ isLoading, text }) => (
  <button
    type="submit"
    disabled={isLoading}
    className={clsx(
      "w-full flex justify-center items-center py-3 px-4 text-sm font-medium rounded-xl text-white bg-sedaily-500 transition-all",
      isLoading ? "opacity-70 cursor-not-allowed" : "hover:bg-sedaily-600 active:scale-[0.98]"
    )}
  >
    {isLoading && (
      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    )}
    {text}
  </button>
);

export default SignUpPage;
